import { useCallback, useEffect, useState } from "react";
import apiClient from "@/services/apiClient";
import DataListPage from "./DataListPage";
import ToastHelper from "@/utils/toastHelper";
import { Switch } from "@/components/ui/switch";

const STATUS_STYLES = {
  Active: "bg-emerald-50 text-emerald-700 border border-emerald-200",
  Inactive: "bg-rose-50 text-rose-700 border border-rose-200",
};
const emptyCourse = {
  courseId: 0,
  courseCode: "",
  courseName: "",
  courseDescription: "",
  duration: "",
  deliveryMode: "",
  price: "",
  isDisabled: false,
  isDeleted: false,
};

const normalizeCourses = (payload) => {
  const list = Array.isArray(payload)
    ? payload
    : (payload?.data ??
      payload?.result ??
      payload?.items ??
    payload?.Courses ??
      payload?.courses ??
      []);
  return list.map((course, index) => ({
    id: course.courseId ?? course.CourseId ?? `C-${index + 1}`,
    name: course.courseName ?? course.CourseName ?? "Unknown",
    duration: course.duration ?? course.Duration ?? "",
    trainer: course.trainerName ?? course.TrainerName ?? "TBA",
    seats: course.capacity ?? course.Capacity ?? "",
    price: course.fee ?? course.Fee ?? course.price ?? course.Price ?? 0,
    status: course.isDisabled || course.IsDisabled ? "Inactive" : "Active",
  }));
};

function CourseForm({ item, onClose, onSaved }) {
  const [form, setForm] = useState(item);
  const [saving, setSaving] = useState(false);
  const change = (key, value) =>
    setForm((current) => ({ ...current, [key]: value }));

  const save = async (event) => {
    event.preventDefault();
    setSaving(true);
    try {
      const payload = { ...form, price: Number(form.price) };
      const response = form.courseId
        ? await apiClient.put(`/AdminCourse/${form.courseId}`, payload)
        : await apiClient.post("/AdminCourse", payload);
      ToastHelper.success(
        form.courseId
          ? "Course updated successfully."
          : "Course created successfully.",
      );
      onSaved(response.data);
    } catch (error) {
      ToastHelper.apiError(error, "Unable to save course");
    } finally {
      setSaving(false);
    }
  };

  return (
    <div
      className="fixed inset-0 z-50 flex items-center justify-center bg-slate-950/50 p-4"
      onMouseDown={(event) => event.target === event.currentTarget && onClose()}
    >
      <form
        onSubmit={save}
        className="max-h-[94vh] w-full max-w-3xl overflow-y-auto rounded-xl bg-white shadow-2xl"
      >
        <div className="sticky top-0 flex items-center justify-between border-b bg-white px-6 py-4">
          <h2 className="text-xl font-display font-extrabold text-[#0A2540]">
            {form.courseId ? "Edit course" : "New course"}
          </h2>
          <button
            type="button"
            className="text-2xl text-slate-400"
            onClick={onClose}
          >
            ×
          </button>
        </div>
        <div className="grid gap-4 p-6 sm:grid-cols-2">
          <label>
            <span className="wc-label">Course code</span>
            <input
              required
              className="wc-input"
              value={form.courseCode}
              onChange={(event) => change("courseCode", event.target.value)}
            />
          </label>
          <label>
            <span className="wc-label">Course name</span>
            <input
              required
              className="wc-input"
              value={form.courseName}
              onChange={(event) => change("courseName", event.target.value)}
            />
          </label>
          <label>
            <span className="wc-label">Duration</span>
            <input
              required
              className="wc-input"
              value={form.duration}
              onChange={(event) => change("duration", event.target.value)}
            />
          </label>
          <label>
            <span className="wc-label">Delivery mode</span>
            <input
              required
              className="wc-input"
              value={form.deliveryMode}
              onChange={(event) => change("deliveryMode", event.target.value)}
            />
          </label>
          <label>
            <span className="wc-label">Course fee</span>
            <input
              required
              min="0.01"
              step="0.01"
              type="number"
              className="wc-input"
              value={form.price}
              onChange={(event) => change("price", event.target.value)}
            />
          </label>
          <label className="flex items-end gap-3 pb-2">
            <Switch
              checked={!form.isDisabled}
              onCheckedChange={(checked) => change("isDisabled", !checked)}
              className="data-[state=checked]:bg-emerald-500 data-[state=unchecked]:bg-rose-400"
            />
            <span className="text-sm font-semibold text-slate-700">
              {form.isDisabled ? "Inactive" : "Active"}
            </span>
          </label>
          <label className="sm:col-span-2">
            <span className="wc-label">Description</span>
            <textarea
              className="wc-input min-h-28"
              value={form.courseDescription}
              onChange={(event) =>
                change("courseDescription", event.target.value)
              }
            />
          </label>
        </div>
        <div className="flex justify-end gap-3 border-t px-6 py-4">
          <button type="button" className="wc-btn-outline" onClick={onClose}>
            Cancel
          </button>
          <button disabled={saving} className="wc-btn-primary">
            {saving ? "Saving..." : "Save course"}
          </button>
        </div>
      </form>
    </div>
  );
}

export default function AdminCourses() {
  const [rows, setRows] = useState([]);
  const [editing, setEditing] = useState(null);
  const loadCourses = useCallback(async () => {
    try {
      const response = await apiClient.get("/AdminCourse/GetCourses");
      setRows(normalizeCourses(response.data));
    } catch (error) {
      setRows([]);
      ToastHelper.apiError(error, "Unable to load courses");
    }
  }, []);
  useEffect(() => {
    loadCourses();
  }, [loadCourses]);

  const editCourse = async (row) => {
    try {
      const response = await apiClient.get(`/AdminCourse/${row.id}`);
      const course = response.data;
      setEditing({
        ...emptyCourse,
        ...course,
        courseId: course.courseId ?? course.CourseId,
        courseCode: course.courseCode ?? course.CourseCode ?? "",
        courseName: course.courseName ?? course.CourseName ?? "",
        courseDescription:
          course.courseDescription ?? course.CourseDescription ?? "",
        deliveryMode: course.deliveryMode ?? course.DeliveryMode ?? "",
        duration: course.duration ?? course.Duration ?? "",
        price: course.price ?? course.Price ?? 0,
        isDisabled: course.isDisabled ?? course.IsDisabled ?? false,
      });
    } catch (error) {
      ToastHelper.apiError(error, "Unable to load course details");
    }
  };

  return (
    <>
      <DataListPage
        title="Courses"
        subtitle="Course catalogue"
        testidPrefix="courses"
        rows={rows}
        statusStyles={STATUS_STYLES}
        rowClassName={(row) => row.status === "Inactive" ? "bg-slate-100 text-slate-500 hover:bg-slate-200" : ""}
        actions={[{ label: "View / Edit", fn: editCourse }]}
        columns={[
          { key: "id", label: "Code" },
          {
            key: "name",
            label: "Course",
            render: (row) => <span className="font-semibold">{row.name}</span>,
          },
          { key: "duration", label: "Duration" },
          { key: "trainer", label: "Trainer" },
          { key: "seats", label: "Capacity" },
          { key: "price", label: "Fee", type: "money", align: "right" },
          { key: "status", label: "Status", type: "status" },
        ]}
      />
      {editing && (
        <CourseForm
          item={editing}
          onClose={() => setEditing(null)}
          onSaved={() => {
            setEditing(null);
            loadCourses();
          }}
        />
      )}
    </>
  );
}
